import { Tag } from "@blueprintjs/core"
import { useFormikContext } from "formik"
import { useCallback, useMemo, useState } from "react"
import { FlexContainer } from "../../../../styled/flexbox.styled"
import CompanySuggest from "../../../components/CompanySuggest"
import { TradingCompany } from "../../Config/types"
import { IdeaType } from "../types/business"
import { IdeaCompany } from "../types/company"
import { IIdeaCreateForm } from "./IdeaCreate"
import { IdeaSettingsInnerContainer } from "./IdeaCreate.styled"

interface IIdeaCreateRelatedCompaniesSettingsProps {
  disabled: boolean
}

const IdeaCreateRelatedCompaniesSettings = ({
  disabled,
}: IIdeaCreateRelatedCompaniesSettingsProps) => {
  const { setFieldValue, values } = useFormikContext<IIdeaCreateForm>()

  const [selectedCompany, setSelectedCompany] = useState<TradingCompany | null>(null)

  /**
   * Adds a company to the list if it doesn't already exist.
   */
  const handleCompanySelect = useCallback(
    (company: TradingCompany) => {
      const companyExists = values.related_companies?.some(
        existing => existing.id === company.id,
      )
      if (!companyExists && values.related_companies) {
        const newCompany: IdeaCompany = {
          id: company.id,
          name: company.name,
          bbg_ticker: company.bbg_ticker,
        }
        const updatedCompanies = [...values.related_companies, newCompany]
        setFieldValue("related_companies", updatedCompanies)
      }
      setSelectedCompany(null)
    },
    [values.related_companies, setFieldValue],
  )

  /**
   * Removes a company from the list based on its ID.
   */
  const handleTagRemove = useCallback(
    (companyId?: number) => {
      const updatedCompanies = values.related_companies?.filter(
        company => company.id !== companyId,
      )
      setFieldValue("related_companies", updatedCompanies)
    },
    [values.related_companies, setFieldValue],
  )

  const shouldDisplaySuggest = useMemo(() => {
    if (values.related_companies) {
      return (
        (values.idea_type === IdeaType.STRUCTURAL && values.related_companies.length < 1) ||
        values.idea_type === IdeaType.NOTE
      )
    }
    return false
  }, [values.idea_type, values.related_companies])

  return (
    <IdeaSettingsInnerContainer flexDirection="column">
      {shouldDisplaySuggest && (
        <CompanySuggest
          selectedCompany={selectedCompany}
          onCompanySelect={handleCompanySelect}
          placeholder={
            "Link a Company" + (values.idea_type === IdeaType.NOTE ? " (Optional)" : "")
          }
        />
      )}
      <FlexContainer flexWrap="wrap" flexDirection="row" gap="10px">
        {values.related_companies?.map(company => (
          <Tag
            key={company.id}
            onRemove={disabled ? undefined : () => handleTagRemove(company.id)}
          >
            {company.bbg_ticker}
          </Tag>
        ))}
      </FlexContainer>
    </IdeaSettingsInnerContainer>
  )
}

export default IdeaCreateRelatedCompaniesSettings
